import {
  useReactTable,
  flexRender,
  getCoreRowModel,
  type ColumnDef,
} from "@tanstack/react-table";
import type { CSSProperties, ReactNode } from "react";
import { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from "react";
import { RenderPropSticky } from "react-sticky-el";
import type { PaginatedResponse } from "../../../api/client/types/common.types";
import { LoadingSpinner } from "./LoadingSpinner";

export interface PaginatedTableProps<T extends object> {
  data: PaginatedResponse<T> | undefined;
  columns: ColumnDef<T>[];
  isLoading?: boolean;
  isError?: boolean;
  page: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
  totalLabel?: string;
  entityName?: string;
  /** Optional custom total count that overrides data?.total when provided */
  customTotal?: number;
  /** Optional classes for the scroll/clip container around the table */
  tableContainerClassName?: string;
  /** Optional classes for table rows */
  rowClassName?: string;
  /** Optional content rendered right below total count */
  afterTotalContent?: ReactNode;
  /** When true, draws borders around every table cell (th and td) */
  showCellBorders?: boolean;
}

export function PaginatedTable<T extends object>({
  data,
  columns,
  isLoading,
  isError,
  page,
  pageSize,
  onPageChange,
  onPageSizeChange,
  totalLabel = "Total",
  entityName = "elementos",
  customTotal,
  tableContainerClassName,
  rowClassName,
  afterTotalContent,
  showCellBorders = false,
}: PaginatedTableProps<T>) {
  const tableBorderClass = showCellBorders ? "border-collapse border border-gray-300" : "";
  const cellBorderClass = showCellBorders ? "border border-gray-300" : "";
  const tableBoundaryId = useId().replace(/:/g, "");
  const containerRef = useRef<HTMLDivElement | null>(null);
  const tableRef = useRef<HTMLTableElement | null>(null);
  const stickyTableRef = useRef<HTMLTableElement | null>(null);
  const [headerCellWidths, setHeaderCellWidths] = useState<number[]>([]);
  const [headerBackgroundColor, setHeaderBackgroundColor] = useState("rgb(243 244 246)");
  const [tableScrollWidth, setTableScrollWidth] = useState(0);
  const [containerWidth, setContainerWidth] = useState(0);
  const [containerScrollLeft, setContainerScrollLeft] = useState(0);
  const [containerChromeStyle, setContainerChromeStyle] = useState<CSSProperties>({});
  const [stickyInitVersion, setStickyInitVersion] = useState(0);

  const syncStickyHeaderStyles = useCallback(() => {
    const sourceTable = tableRef.current;
    const targetTable = stickyTableRef.current;

    if (!sourceTable || !targetTable) {
      return;
    }

    const sourceThead = sourceTable.querySelector("thead");
    const targetThead = targetTable.querySelector("thead");

    if (sourceThead && targetThead) {
      const sourceTheadStyles = window.getComputedStyle(sourceThead);
      for (const propName of sourceTheadStyles) {
        targetThead.style.setProperty(
          propName,
          sourceTheadStyles.getPropertyValue(propName),
          sourceTheadStyles.getPropertyPriority(propName)
        );
      }
    }

    const sourceCells = Array.from(
      sourceTable.querySelectorAll("thead th")
    ) as HTMLTableCellElement[];
    const targetCells = Array.from(
      targetTable.querySelectorAll("thead th")
    ) as HTMLTableCellElement[];

    targetCells.forEach((targetCell, index) => {
      const sourceCell = sourceCells[index];
      if (!sourceCell) {
        return;
      }

      const sourceStyles = window.getComputedStyle(sourceCell);
      for (const propName of sourceStyles) {
        targetCell.style.setProperty(
          propName,
          sourceStyles.getPropertyValue(propName),
          sourceStyles.getPropertyPriority(propName)
        );
      }
    });
  }, []);

  const measureHeader = useCallback(() => {
    const tableElement = tableRef.current;
    if (!tableElement) {
      return;
    }

    const headerCells = Array.from(
      tableElement.querySelectorAll("thead th")
    ) as HTMLTableCellElement[];
    const nextWidths = headerCells.map((cell) => cell.getBoundingClientRect().width);
    const firstHeaderStyles = headerCells[0]
      ? window.getComputedStyle(headerCells[0])
      : null;

    const theadElement = tableElement.querySelector("thead");
    const theadStyles = theadElement ? window.getComputedStyle(theadElement) : null;
    const containerElement = containerRef.current;

    setHeaderCellWidths(nextWidths);
    setHeaderBackgroundColor(
      firstHeaderStyles?.backgroundColor ??
        theadStyles?.backgroundColor ??
        "rgb(243 244 246)"
    );
    setTableScrollWidth(tableElement.scrollWidth);
    if (containerElement) {
      const containerStyles = window.getComputedStyle(containerElement);
      setContainerWidth(containerElement.getBoundingClientRect().width);
      setContainerScrollLeft(containerElement.scrollLeft);
      setContainerChromeStyle({
        borderTop: containerStyles.borderTop,
        borderRight: containerStyles.borderRight,
        borderLeft: containerStyles.borderLeft,
        borderTopLeftRadius: containerStyles.borderTopLeftRadius,
        borderTopRightRadius: containerStyles.borderTopRightRadius,
        boxSizing: containerStyles.boxSizing as CSSProperties["boxSizing"],
      });
    }
  }, []);

  useLayoutEffect(() => {
    measureHeader();

    const tableElement = tableRef.current;
    const containerElement = containerRef.current;

    if (!tableElement || !containerElement || typeof ResizeObserver === "undefined") {
      return;
    }

    const observer = new ResizeObserver(() => {
      measureHeader();
    });

    observer.observe(tableElement);
    observer.observe(containerElement);

    return () => {
      observer.disconnect();
    };
  }, [measureHeader]);

  useEffect(() => {
    syncStickyHeaderStyles();
  }, [syncStickyHeaderStyles, headerCellWidths, data, page, pageSize]);

  useEffect(() => {
    const containerElement = containerRef.current;
    if (!containerElement) {
      return;
    }

    const handleContainerScroll = () => {
      setContainerScrollLeft(containerElement.scrollLeft);
    };

    window.addEventListener("resize", measureHeader);
    window.addEventListener("orientationchange", measureHeader);
    containerElement.addEventListener("scroll", handleContainerScroll, {
      passive: true,
    });

    measureHeader();

    return () => {
      window.removeEventListener("resize", measureHeader);
      window.removeEventListener("orientationchange", measureHeader);
      containerElement.removeEventListener("scroll", handleContainerScroll);
    };
  }, [measureHeader]);

  useEffect(() => {
    const containerElement = containerRef.current;
    if (!containerElement) {
      return;
    }

    let initializedForVisibleRender = false;

    const refreshStickyOnVisible = () => {
      if (initializedForVisibleRender) {
        return;
      }

      const rect = containerElement.getBoundingClientRect();
      if (rect.width <= 0 || rect.height <= 0) {
        return;
      }

      initializedForVisibleRender = true;
      measureHeader();
      requestAnimationFrame(() => {
        measureHeader();
        syncStickyHeaderStyles();
        // Ensure sticky initializes the first time the table becomes visible.
        setStickyInitVersion((current) => current + 1);
      });
    };

    refreshStickyOnVisible();

    const visibilityObserver =
      typeof IntersectionObserver !== "undefined"
        ? new IntersectionObserver(
            (entries) => {
              if (entries.some((entry) => entry.isIntersecting || entry.intersectionRatio > 0)) {
                refreshStickyOnVisible();
              }
            },
            { threshold: [0, 0.01] }
          )
        : null;

    if (visibilityObserver) {
      visibilityObserver.observe(containerElement);
    }

    const sizeObserver =
      typeof ResizeObserver !== "undefined"
        ? new ResizeObserver(() => {
            refreshStickyOnVisible();
          })
        : null;

    if (sizeObserver) {
      sizeObserver.observe(containerElement);
    }

    return () => {
      visibilityObserver?.disconnect();
      sizeObserver?.disconnect();
    };
  }, [data?.items?.length, measureHeader, syncStickyHeaderStyles]);

  const table = useReactTable({
    data: data?.items ?? [],
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  if (isLoading)
    return (
      <LoadingSpinner className="py-10" size="md" label="Cargando datos..." />
    );
  if (isError)
    return (
      <div className="text-center py-6 text-red-500">
        Error al cargar los datos.
      </div>
    );

  const totalPages = data?.total_pages ?? 1;

  return (
    <div className="w-full">
      {/* Total count at the top */}
      <div className="mb-3 text-sm font-bold text-lifeguards-blue text-left">
        {totalLabel}: {customTotal !== undefined ? customTotal : data?.total ?? 0} {entityName}
      </div>
      {afterTotalContent}

      <div id={tableBoundaryId} className="sticky-table-boundary">
        {/* iOS fix: CSS-sticky cover blocks any content that bleeds into the safe-area/header gap */}
        <div className="sticky-top-cover" aria-hidden="true" />
        {headerCellWidths.length > 0 && (
        <RenderPropSticky
          key={stickyInitVersion}
          disabled={false}
          topOffset={0}
          mode="top"
          boundaryElement={`#${tableBoundaryId}`}
          hideOnBoundaryHit
          scrollElement="window"
          offsetTransforms={false}
          positionRecheckInterval={16}
          isIOSFixEnabled
          dontUpdateHolderHeightWhenSticky
        >
          {({ holderRef, wrapperRef, wrapperStyles, holderStyles, isFixed }) => (
            <div
              ref={holderRef}
              style={{
                ...holderStyles,
                height: 0,
                minHeight: 0,
              }}
            >
              <div
                ref={wrapperRef}
                className="sticky-header-wrapper"
                style={{
                  ...(wrapperStyles ?? {}),
                  top: isFixed
                    ? "var(--table-sticky-top-effective, var(--table-sticky-top, 0px))"
                    : wrapperStyles?.top,
                  zIndex: 60,
                  pointerEvents: "none",
                  width: containerWidth,
                  backgroundColor: headerBackgroundColor,
                  overflow: "visible",
                  ...containerChromeStyle,
                  opacity: isFixed ? 1 : 0,
                  visibility: isFixed ? "visible" : "hidden",
                  transform: "translateZ(0)",
                  WebkitTransform: "translateZ(0)",
                  willChange: "transform, top",
                }}
              >
                <div
                  aria-hidden="true"
                  className="sticky-header-overlay"
                  style={{ backgroundColor: headerBackgroundColor }}
                >
                  <table
                    ref={stickyTableRef}
                    className={`min-w-full text-sm text-left sticky-header-table ${tableBorderClass}`}
                    style={{
                      width: tableScrollWidth,
                      transform: `translateX(-${containerScrollLeft}px)`,
                    }}
                  >
                    <thead className="bg-gray-100 text-gray-700 uppercase text-xs">
                      {table.getHeaderGroups().map((headerGroup) => {
                        let headerIndex = 0;

                        return (
                          <tr key={`sticky-${headerGroup.id}`}>
                            {headerGroup.headers.map((header) => {
                              const width = headerCellWidths[headerIndex] ?? 0;
                              headerIndex += 1;

                              return (
                                <th
                                  key={`sticky-${header.id}`}
                                  className={`px-2.5 py-2 whitespace-nowrap ${cellBorderClass}`}
                                  style={{
                                    width,
                                    minWidth: width,
                                    maxWidth: width,
                                  }}
                                >
                                  {flexRender(
                                    header.column.columnDef.header,
                                    header.getContext()
                                  )}
                                </th>
                              );
                            })}
                          </tr>
                        );
                      })}
                    </thead>
                  </table>
                </div>
              </div>
            </div>
          )}
        </RenderPropSticky>
        )}

        <div
          ref={containerRef}
          className={`${tableContainerClassName ?? "overflow-x-auto border rounded-lg"} sticky-header-container overflow-y-visible`}
        >
          <table ref={tableRef} className={`min-w-full text-sm text-left sticky-header-table ${tableBorderClass}`}>
            <thead className="bg-gray-100 text-gray-700 uppercase text-xs">
              {table.getHeaderGroups().map((headerGroup) => (
                <tr key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <th
                      key={header.id}
                      className={`px-2.5 py-2 whitespace-nowrap ${cellBorderClass}`}
                    >
                      {flexRender(
                        header.column.columnDef.header,
                        header.getContext()
                      )}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody>
              {table.getRowModel().rows.map((row) => (
                <tr
                  key={row.id}
                  className={`${showCellBorders ? "" : "border-t"} hover:bg-gray-50 transition-colors ${rowClassName ?? ""}`}
                >
                  {row.getVisibleCells().map((cell) => (
                    <td
                      key={cell.id}
                      className={`px-2.5 py-0.5 ${cellBorderClass}`}
                    >
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination Controls */}
      <div className="flex flex-col sm:flex-row justify-between items-center gap-3 p-3 border-t bg-gray-50 text-sm mt-3 rounded-b-lg">
        <div className="flex items-center gap-2">
          <span>Filas por página:</span>
          <select
            value={pageSize}
            onChange={(e) => onPageSizeChange(Number(e.target.value))}
            className="border rounded px-2 py-1 text-sm"
          >
            {[5, 10, 20, 50, 100].map((size) => (
              <option key={size} value={size}>
                {size}
              </option>
            ))}
          </select>
        </div>

        <div className="flex items-center gap-4">
          <button
            className="px-3 py-1 border rounded disabled:opacity-50"
            onClick={() => onPageChange(Math.max(page - 1, 1))}
            disabled={page === 1}
          >
            Anterior
          </button>

          <span>
            Página {page} de {totalPages}
          </span>

          <button
            className="px-3 py-1 border rounded disabled:opacity-50"
            onClick={() => onPageChange(Math.min(page + 1, totalPages))}
            disabled={page >= totalPages}
          >
            Siguiente
          </button>
        </div>
      </div>
    </div>
  );
}
